import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { GridModalComponent } from '@app/core/components/grid/grid.decorators';
import { KendoBaseComponent } from '@app/core/components/kendo-base/kendo-base.component';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DATE_FORMATS } from '@app/core/models/date-adapter';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@app/core/services/translate.service';
import { StateDialogContext, TT_STATE_DIALOG } from '@app/modal/state-modal/state-modal.component';
import { EventStyleArgs, IsSlotSelectedArgs, SchedulerEvent, SlotRange } from '@progress/kendo-angular-scheduler';

@GridModalComponent('calview')
@Component({
    selector: 'tt-cal-view',
    templateUrl: './cal-view.component.html',
    styleUrls: ['./cal-view.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class CalViewComponent extends KendoBaseComponent implements OnChanges {
    @Input()
    public params?: CalViewParams;

    public get?: CalViewGet;

    @Output()
    public getLoaded = new EventEmitter<CalViewGet>();

    public months: Date[] = [];

    public events: CalViewEvent[] = [];

    public activeCode?: OffDutyCodeItem;

    public selectable = false;

    public offDutyCodes: OffDutyCodeItem[] = [];

    public ready = false;

    public saving = false;

    public transferring = false;

    public refreshing = false;

    public pendingChanges = false;

    public translations = {
        clear: '',
        you_have_unsaved_changes: '',
        calview_clear_tooltip: '',
    };

    constructor(
        private datatask: DataTaskService,
        private translate: TranslateService,
        private modal: ModalService,
        private toast: ToastService,
        @Optional() @Inject(TT_STATE_DIALOG) stateDialog: StateDialogContext,
    ) {
        super();

        this.initFromDialog(stateDialog);
    }

    public eventStyles = (args: EventStyleArgs) => {
        const id = args.event.id.split('.')[0];
        const offDutyCode = this.offDutyCodes.find((code) => code.offdutycode_keyno === id);

        return { padding: 0, backgroundColor: offDutyCode?.background_color_hex, color: offDutyCode?.text_color_hex };
    };

    public onSlotSelected(event: SlotRange) {
        console.log(event);
        if (!this.get || !this.activeCode) return;

        const startDate = new Date(event.start);
        const endDate = new Date(event.end);

        let loopDate = new Date(startDate);

        while (loopDate < endDate) {
            const existingIndex = this.events.findIndex((event) => event.id.split('.')[1] === new Date(loopDate.getTime() - loopDate.getTimezoneOffset() * 60000).toISOString().substring(0, 10));
            console.log(existingIndex);
            if (existingIndex > -1) {
                this.pendingChanges = true;
                this.events.splice(existingIndex, 1);
            }

            if (this.activeCode?.offdutycode_keyno > '0') {
                this.pendingChanges = true;

                this.events.push({
                    id: this.getIdForEvent({ offdutycode_keyno: this.activeCode.offdutycode_keyno, date: loopDate }),
                    start: new Date(loopDate.getTime()),
                    end: new Date(loopDate.getTime()),
                    isAllDay: true,
                    title: this.activeCode.offdutycode_name,
                    offdutycode_keyno: this.activeCode.offdutycode_keyno,
                    offdutycode_name: this.activeCode.offdutycode_name,
                    p2_offdutydata_keyno: '0',
                    powtime_keyno: '0',
                    icons: [],
                    tooltip: this.getTooltip({ date: new Date(loopDate.getTime()), title: this.activeCode.offdutycode_name }),
                    // ...this.get,
                });
            }

            loopDate.setDate(loopDate.getDate() + 1);
        }

        this.events = [...this.events];
    }

    private getTooltip(event: { title: string; date: Date }): string {
        return new DatePipe(DATE_FORMATS.display.dateInput.language).transform(new Date(event.date.getTime()), 'EEEE, MMMM d, y') + ' - ' + event.title;
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;
            const eventDTOs: CalViewEventDTO[] = this.events.map((event) => ({
                powtime_keyno: event.powtime_keyno,
                p2_offdutydata_keyno: event.p2_offdutydata_keyno,
                offdutycode_keyno: event.offdutycode_keyno,
                offdutycode_name: event.offdutycode_name,
                start: new Date(event.start.getTime() - event.start.getTimezoneOffset() * 60000).toISOString().substring(0, 10),
                end: new Date(event.end.getTime() - event.end.getTimezoneOffset() * 60000).toISOString().substring(0, 10),
            }));

            const { errorcode, errormessage } = await this.saveEvents({ ...this.get, events: eventDTOs });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.toast.addToast({ id: 'calview.save.' + Date.now(), type: 'success', title: 'saved!', translateTitle: true, message: 'changes_have_been_saved', translateMessage: true });
                this.pendingChanges = false;
            }

            this.loadEvents();
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async transfer() {
        if (!this.get) return;

        try {
            this.transferring = true;

            const { errorcode, errormessage } = await this.transferToPowtime(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.toast.addToast({ id: 'calview.save.' + Date.now(), type: 'success', title: 'saved!', translateTitle: true, message: 'changes_have_been_saved', translateMessage: true });
            }

            this.loadEvents();
        } catch (error) {
            console.error(error);
        } finally {
            this.transferring = false;
        }
    }

    public async refresh() {
        if (!this.get) return;

        try {
            this.refreshing = true;

            await this.loadEvents();
        } catch (error) {
            console.error(error);
        } finally {
            this.refreshing = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.getCalView(this.params);
            this.getLoaded.emit(this.get);
        }
    }

    private async loadOffDutyCodes() {
        this.offDutyCodes = await this.getOffDutyCodes();
    }

    public async loadEvents() {
        if (!!this.get) {
            const newEvents = (await this.getEvents(this.get)).map((event) => ({ ...event, id: this.getIdForEvent({ offdutycode_keyno: event.offdutycode_keyno, date: event.start }), title: event.offdutycode_name, start: new Date(event.start), end: new Date(event.end), icons: this.getIconsForEvent(event), tooltip: this.getTooltip({ title: event.offdutycode_name, date: new Date(event.start) }) }));

            for (let event of newEvents) {
                if (!this.events.find((e) => e.id === event.id)) {
                    this.events.push(event);
                } else {
                    const index = this.events.findIndex((e) => e.id === event.id);

                    if (index !== -1) {
                        this.events[index] = event;
                    }
                }
            }

            this.events = [...this.events];
        }
    }

    private async saveEvents(params: SaveCalViewEventParams): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3915, params))[0];
    }

    private async transferToPowtime(params: CalViewGet): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3929, params))[0];
    }

    private async getCalView(params: CalViewParams): Promise<CalViewGet> {
        return (await this.datatask.Post(3914, params))[0];
    }

    private getOffDutyCodes(): Promise<OffDutyCodeItem[]> {
        return this.datatask.Post(314);
    }

    private getEvents(params: CalViewGet): Promise<CalViewEventDTO[]> {
        return this.datatask.Post(3916, params);
    }

    private async translateWords() {
        const data = await this.translate.translateBatch(this.translations);

        for (let key in data) {
            this.translations[key as keyof typeof this.translations] = data[key as keyof typeof this.translations];
        }
    }

    private async init() {
        this.ready = false;

        try {
            this.translateWords();
            await Promise.all([this.loadGet(), this.loadOffDutyCodes()]);
            this.loadEvents();
            this.generateMonthsForSelectedYear();
        } catch (error) {
            console.error(error);
        } finally {
            this.ready = true;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    private async initFromDialog(state?: StateDialogContext) {
        if (!!state) {
            state.setSize({ width: '100vw', height: '100vh' });
            this.params = state.params as unknown as CalViewParams;
            await this.init();
            state.setHeading(this.get?.webpage_heading ?? '');
        }
    }

    private getIdForEvent(params: { offdutycode_keyno: string; date: string | Date }): string {
        if (typeof params.date !== 'string' && params.date.toString() !== 'Invalid Date') {
            params.date = new Date(params.date.getTime() - params.date.getTimezoneOffset() * 60000).toISOString().substring(0, 10);
        }

        return `${params.offdutycode_keyno}.${params.date}`;
    }

    private getIconsForEvent(event: CalViewEventDTO): { icon: string; color: string }[] {
        let icons: { icon: string; color: string }[] = [];

        const iconsString = event.item_glyphicon?.split(' ') ?? [];
        const iconColors = event.item_glyphicon_color?.split(' ');

        if (!!iconsString && !!iconColors) {
            icons = iconsString.map((icon, index) => ({
                icon: this.getIcon(icon),
                color: iconColors[index] ?? 'var(--tt-text-color)',
            }));
        }

        return icons;
    }

    private getIcon(icon: string) {
        if (!!icon && typeof icon === 'string') {
            if (icon.startsWith('fa') && [2, 3].includes(icon.indexOf('-'))) {
                icon = `${icon.slice(0, icon.indexOf('-'))} fa${icon.slice(icon.indexOf('-'))}`;
            } else {
                icon = 'glyphicon ' + icon;
            }
        }

        return icon;
    }

    public generateMonthsForSelectedYear() {
        if (!this.get) return;

        this.months = [];

        for (let i = 1; i <= 12; i++) {
            this.months.push(new Date(`${this.get.year}-${i}-05`));
        }
    }

    private startOfIsoWeek(d: Date): Date {
        const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const day = x.getDay() || 7;

        x.setDate(x.getDate() - (day - 1));
        x.setHours(0, 0, 0, 0);

        return x;
    }

    private isoWeekNumber(date: Date): number {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;

        d.setUTCDate(d.getUTCDate() + 4 - dayNum);

        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        const diffDays = Math.floor((d.getTime() - yearStart.getTime()) / 86400000) + 1;

        return Math.ceil(diffDays / 7);
    }

    weekNumbersForMonth(month: Date): number[] {
        const first = this.startOfIsoWeek(new Date(month.getFullYear(), month.getMonth(), 1));

        return Array.from({ length: 6 }, (_, i) => {
            const weekStart = new Date(first);
            weekStart.setDate(first.getDate() + i * 7);
            return this.isoWeekNumber(weekStart);
        });
    }
}

interface SaveCalViewEventParams extends CalViewGet {
    events: CalViewEventDTO[];
}

interface CalViewEvent extends SchedulerEvent {
    offdutycode_keyno: string;
    offdutycode_name: string;
    p2_offdutydata_keyno: string;
    powtime_keyno: string;
    tooltip: string;
    icons: { icon: string; color: string }[];
}

interface CalViewEventDTO {
    start: string;
    end: string;
    offdutycode_keyno: string;
    offdutycode_name: string;
    p2_offdutydata_keyno: string;
    powtime_keyno: string;
    item_glyphicon?: string;
    item_glyphicon_color?: string;
}

export interface CalViewParams {
    viewtype: string;
    argtype: string;
    argvalue: string;
}

interface CalViewGet {
    webpage_heading: string;
    year: string | number;
    viewtype: string;
    argtype: string;
    argvalue: string;
    show_transfer_button: '1' | '0';
}

interface OffDutyCodeItem {
    offdutycode_keyno: string;
    offdutycode_name: string;
    background_color_hex: string;
    text_color_hex: string;
    orderby: string;
}
